/**
 * Auth store (Zustand).
 *
 * Source of truth for the admin UI's authentication state:
 *   - Tokens are mirrored to localStorage so they survive reloads.
 *   - The HTTP interceptor in `lib/api.ts` reads the access token directly
 *     from localStorage (keeps the store & transport modules decoupled), but
 *     calls back into this store via `registerAuthCallbacks` to persist
 *     rotated tokens after a 401 refresh.
 *   - `hydrate()` is called on app mount: if a token is present we call
 *     `/admin/auth/me` to rehydrate the user; if that 401s the interceptor
 *     will already have cleaned up.
 */

import { create } from 'zustand';

import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  apiClient,
  registerAuthCallbacks,
} from '@/lib/api';

export type AdminRole = 'admin' | 'operator' | 'viewer';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
}

interface LoginResponse {
  accessToken: string;
  accessTokenExpiresIn: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: AdminUser;
}

interface MeResponse {
  user: AdminUser;
}

interface AuthState {
  user: AdminUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  hydrated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (access: string, refresh: string) => void;
  setUser: (user: AdminUser | null) => void;
  hydrate: () => Promise<void>;
}

function readStoredUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: readStoredUser(),
  accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
  refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  hydrated: false,

  async login(email, password) {
    const data = await apiClient.post<LoginResponse>('/admin/auth/login', {
      email,
      password,
    });
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
      hydrated: true,
    });
  },

  async logout() {
    const { refreshToken } = get();
    // Best-effort revoke; ignore errors so the client always logs out locally.
    if (refreshToken) {
      try {
        await apiClient.post('/admin/auth/logout', { refreshToken });
      } catch {
        /* ignore */
      }
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    set({ accessToken: null, refreshToken: null, user: null });
  },

  setTokens(access, refresh) {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    set({ accessToken: access, refreshToken: refresh });
  },

  setUser(user) {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    set({ user });
  },

  async hydrate() {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) {
      set({ hydrated: true });
      return;
    }
    try {
      const data = await apiClient.get<MeResponse>('/admin/auth/me');
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({ user: data.user, hydrated: true });
    } catch {
      // Interceptor already attempted a refresh; if we're still here, tokens
      // are dead. Clear local state.
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      set({ accessToken: null, refreshToken: null, user: null, hydrated: true });
    }
  },
}));

// Wire axios interceptor callbacks into this store so rotated tokens from
// background refreshes flow back into Zustand state (keeps the UI in sync).
registerAuthCallbacks({
  onTokenUpdate: ({ accessToken, refreshToken }) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    useAuthStore.setState({ accessToken, refreshToken });
  },
  onLogout: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    useAuthStore.setState({ accessToken: null, refreshToken: null, user: null });
  },
});
